import { useEffect, useState } from 'react';
import {
  FiUser, FiLock, FiBell, FiSliders, FiAlertTriangle,
  FiEye, FiEyeOff, FiShield, FiMonitor,
} from 'react-icons/fi';
import { getProfile, updateProfile, changePassword } from '../api/authService';
import {
  getNotificationSettings, updateNotificationSettings,
  getActiveSessions, revokeAllSessions, deactivateAccount,
} from '../api/settingsService';
import { useAuth } from '../auth/AuthContext';

/* ─── Helpers ───────────────────────────────────────────── */
const PREF_KEY = 'lms_ui_prefs';
const defaultPrefs = {
  sidebarDefault: 'expanded',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  dateFormat: 'MMM D, YYYY',
  density: 'comfortable',
  primaryColor: '#6366f1',
};

function loadPrefs() {
  try { return { ...defaultPrefs, ...JSON.parse(localStorage.getItem(PREF_KEY) || '{}') }; }
  catch { return defaultPrefs; }
}
function savePrefs(p) { localStorage.setItem(PREF_KEY, JSON.stringify(p)); }

function getInitials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';
}

/* ─── Inline toggle ─────────────────────────────────────── */
function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="toggle-track"><span className="toggle-thumb" /></span>
    </label>
  );
}

/* ─── Password field ────────────────────────────────────── */
function PwField({ name, value, onChange, label, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="stg-pw-field">
      <label className="stg-label">{label}</label>
      <div className="password-field">
        <input
          name={name}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          className="stg-input"
        />
        <button type="button" className="password-toggle" onClick={() => setShow((v) => !v)} aria-label={show ? 'Hide' : 'Show'}>
          {show ? <FiEyeOff size={15} /> : <FiEye size={15} />}
        </button>
      </div>
    </div>
  );
}

/* ─── Status banner ─────────────────────────────────────── */
function StatusBanner({ error, success }) {
  if (!error && !success) return null;
  return (
    <div className={`stg-banner ${error ? 'stg-banner--error' : 'stg-banner--success'}`}>
      {error || success}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 1 — Profile
══════════════════════════════════════════════════════════ */
function ProfileTab() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' });
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getProfile()
      .then((res) => {
        const u = res.data?.user || res.user || {};
        const data = { name: u.name || '', email: u.email || '', phone: u.phone || '', role: u.role || '' };
        setForm(data);
        setOriginal(data);
      })
      .catch((e) => setError(e?.response?.data?.message || 'Could not load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError(''); setSuccess('');
  };

  const isDirty = form.name !== original.name || form.email !== original.email || form.phone !== original.phone;

  const validate = () => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Enter a valid email address.';
    if (form.phone && !/^\+?[\d\s\-().]{7,20}$/.test(form.phone)) return 'Enter a valid phone number.';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await updateProfile({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() });
      setOriginal({ ...form });
      setSuccess('Profile updated successfully.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="stg-loading">Loading profile…</p>;

  return (
    <div className="stg-tab-body">
      <div className="stg-section-header">
        <div className="stg-avatar">{getInitials(form.name)}</div>
        <div>
          <p className="stg-avatar-name">{form.name || 'Your Name'}</p>
          <p className="stg-avatar-role">{form.role || 'User'}</p>
        </div>
      </div>

      <StatusBanner error={error} success={success} />

      <form className="stg-form" onSubmit={onSubmit} noValidate>
        <div className="stg-form-row">
          <div className="stg-field">
            <label className="stg-label">Full Name <span className="required-mark">*</span></label>
            <input className="stg-input" name="name" value={form.name} onChange={onChange} autoComplete="name" />
          </div>
          <div className="stg-field">
            <label className="stg-label">Email Address <span className="required-mark">*</span></label>
            <input className="stg-input" name="email" type="email" value={form.email} onChange={onChange} autoComplete="email" />
          </div>
        </div>
        <div className="stg-form-row">
          <div className="stg-field">
            <label className="stg-label">Phone Number</label>
            <input className="stg-input" name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" placeholder="+1 (555) 000-0000" />
          </div>
          <div className="stg-field">
            <label className="stg-label">Role</label>
            <input className="stg-input stg-input--readonly" value={form.role || '—'} readOnly tabIndex={-1} title="Role is managed by your administrator" />
          </div>
        </div>
        <div className="form-actions">
          <button className="primary-btn" type="submit" disabled={saving || !isDirty}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 2 — Security
══════════════════════════════════════════════════════════ */
function SecurityTab() {
  const { logout } = useAuth();
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const [sessions, setSessions] = useState(null);
  const [sessLoading, setSessLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);
  const [revokeMsg, setRevokeMsg] = useState('');

  useEffect(() => {
    getActiveSessions()
      .then((res) => setSessions(res.data?.sessions || res.sessions || []))
      .catch(() => setSessions(null))
      .finally(() => setSessLoading(false));
  }, []);

  const onPwChange = (e) => {
    setPwForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setPwErrors((p) => ({ ...p, [e.target.name]: '' }));
    setPwError(''); setPwSuccess('');
  };

  const strength = (pw) => {
    if (!pw) return 0;
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', '#f43f5e', '#f59e0b', '#3b82f6', '#10b981'];
  const s = strength(pwForm.newPassword);

  const validatePw = () => {
    const e = {};
    if (!pwForm.currentPassword) e.currentPassword = 'Current password is required.';
    if (!pwForm.newPassword) e.newPassword = 'New password is required.';
    else if (pwForm.newPassword.length < 8) e.newPassword = 'Minimum 8 characters.';
    else if (s < 2) e.newPassword = 'Password is too weak.';
    if (pwForm.newPassword !== pwForm.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    setPwErrors(e);
    return Object.keys(e).length === 0;
  };

  const onPwSubmit = async (e) => {
    e.preventDefault();
    if (!validatePw()) return;
    setPwSaving(true); setPwError(''); setPwSuccess('');
    try {
      await changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwSuccess('Password changed successfully.');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      setPwError(e?.response?.data?.message || 'Password change failed.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('This will sign you out of all devices including this one.')) return;
    setRevoking(true);
    try {
      await revokeAllSessions();
      logout();
    } catch (e) {
      setRevokeMsg(e?.response?.data?.message || 'Could not revoke sessions.');
      setRevoking(false);
    }
  };

  return (
    <div className="stg-tab-body">
      {/* ── Change Password ── */}
      <section className="stg-section">
        <h4 className="stg-section-title"><FiLock size={15} /> Change Password</h4>
        <StatusBanner error={pwError} success={pwSuccess} />
        <form className="stg-form" onSubmit={onPwSubmit} noValidate>
          <PwField name="currentPassword" value={pwForm.currentPassword} onChange={onPwChange} label="Current Password" autoComplete="current-password" />
          {pwErrors.currentPassword && <span className="stg-field-error">{pwErrors.currentPassword}</span>}

          <PwField name="newPassword" value={pwForm.newPassword} onChange={onPwChange} label="New Password" autoComplete="new-password" />
          {pwForm.newPassword && (
            <div className="stg-strength-bar">
              {[1,2,3,4].map((i) => (
                <div key={i} className="stg-strength-seg" style={{ background: i <= s ? strengthColor[s] : '#e2e8f0' }} />
              ))}
              <span className="stg-strength-label" style={{ color: strengthColor[s] }}>{strengthLabel[s]}</span>
            </div>
          )}
          {pwErrors.newPassword && <span className="stg-field-error">{pwErrors.newPassword}</span>}

          <PwField name="confirmPassword" value={pwForm.confirmPassword} onChange={onPwChange} label="Confirm New Password" autoComplete="new-password" />
          {pwErrors.confirmPassword && <span className="stg-field-error">{pwErrors.confirmPassword}</span>}

          <div className="form-actions">
            <button className="primary-btn" type="submit" disabled={pwSaving}>
              {pwSaving ? 'Changing…' : 'Change Password'}
            </button>
          </div>
        </form>
      </section>

      {/* ── Active Sessions ── */}
      <section className="stg-section">
        <h4 className="stg-section-title"><FiMonitor size={15} /> Active Sessions</h4>
        {sessLoading ? (
          <p className="stg-loading">Loading sessions…</p>
        ) : sessions === null ? (
          <p className="stg-muted">Session info unavailable.</p>
        ) : (
          <>
            <div className="stg-sessions-list">
              {sessions.length === 0 && <p className="stg-muted">No active sessions found.</p>}
              {sessions.map((sess, i) => (
                <div key={sess.id || i} className="stg-session-row">
                  <FiShield size={16} className="stg-session-icon" />
                  <div>
                    <p className="stg-session-device">{sess.device || sess.userAgent || 'Unknown device'}</p>
                    <p className="stg-session-meta">{sess.ip || ''}{sess.lastActive ? ` · Last active ${sess.lastActive}` : ''}{sess.isCurrent ? ' · Current session' : ''}</p>
                  </div>
                  {sess.isCurrent && <span className="stg-current-badge">Current</span>}
                </div>
              ))}
            </div>
            {revokeMsg && <p className="error-text" style={{ marginTop: 8 }}>{revokeMsg}</p>}
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button className="danger-btn" onClick={handleRevokeAll} disabled={revoking}>
                {revoking ? 'Revoking…' : 'Sign Out All Devices'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 3 — Notifications
══════════════════════════════════════════════════════════ */
const NOTIF_DEFAULTS = {
  lead_assigned: true,
  lead_converted: true,
  lead_rejected: false,
  lead_reassigned: true,
  agent_created: false,
  daily_digest: true,
  weekly_report: false,
  system_alerts: true,
};

const NOTIF_LABELS = {
  lead_assigned:   { label: 'Lead Assigned to Me',       desc: 'When a new lead is assigned to your account' },
  lead_converted:  { label: 'Lead Converted',             desc: 'When a lead you own is marked as converted' },
  lead_rejected:   { label: 'Lead Rejected',              desc: 'When a lead is rejected or lost' },
  lead_reassigned: { label: 'Lead Reassigned',            desc: 'When one of your leads is reassigned' },
  agent_created:   { label: 'New Agent Created',          desc: 'When a new agent is added to the system' },
  daily_digest:    { label: 'Daily Activity Digest',      desc: 'Daily summary email of your pipeline activity' },
  weekly_report:   { label: 'Weekly Performance Report',  desc: 'Weekly email with your KPIs and metrics' },
  system_alerts:   { label: 'System Alerts',              desc: 'Critical system notifications and announcements' },
};

function NotificationsTab() {
  const [prefs, setPrefs] = useState(NOTIF_DEFAULTS);
  const [original, setOriginal] = useState(NOTIF_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getNotificationSettings()
      .then((res) => {
        const data = { ...NOTIF_DEFAULTS, ...(res.data?.preferences || res.preferences || {}) };
        setPrefs(data); setOriginal(data);
      })
      .catch(() => { /* backend may not have this endpoint yet, use defaults */ })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
    setError(''); setSuccess('');
  };

  const isDirty = JSON.stringify(prefs) !== JSON.stringify(original);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await updateNotificationSettings(prefs);
      setOriginal({ ...prefs });
      setSuccess('Notification preferences saved.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="stg-loading">Loading…</p>;

  const groups = [
    { title: 'Lead Events', keys: ['lead_assigned','lead_converted','lead_rejected','lead_reassigned'] },
    { title: 'Administrative', keys: ['agent_created'] },
    { title: 'Digests & Reports', keys: ['daily_digest','weekly_report'] },
    { title: 'System', keys: ['system_alerts'] },
  ];

  return (
    <div className="stg-tab-body">
      <StatusBanner error={error} success={success} />
      <form onSubmit={onSave}>
        {groups.map((g) => (
          <section className="stg-section" key={g.title}>
            <h4 className="stg-section-title">{g.title}</h4>
            <div className="stg-notif-list">
              {g.keys.map((key) => (
                <div key={key} className="stg-notif-row">
                  <div className="stg-notif-info">
                    <p className="stg-notif-label">{NOTIF_LABELS[key].label}</p>
                    <p className="stg-notif-desc">{NOTIF_LABELS[key].desc}</p>
                  </div>
                  <Toggle checked={prefs[key]} onChange={() => toggle(key)} />
                </div>
              ))}
            </div>
          </section>
        ))}
        <div className="form-actions">
          <button className="primary-btn" type="submit" disabled={saving || !isDirty}>
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 4 — Preferences (UI, stored locally)
══════════════════════════════════════════════════════════ */
const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Dubai',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
];

const DATE_FORMATS = [
  { value: 'MMM D, YYYY', label: 'Jan 1, 2026' },
  { value: 'MM/DD/YYYY',  label: '01/01/2026' },
  { value: 'DD/MM/YYYY',  label: '01/01/2026 (EU)' },
  { value: 'YYYY-MM-DD',  label: '2026-01-01 (ISO)' },
];

function PreferencesTab() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [saved, setSaved] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPrefs((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    setSaved(false);
  };

  const onSave = (e) => {
    e.preventDefault();
    savePrefs(prefs);
    setSaved(true);
  };

  return (
    <div className="stg-tab-body">
      {saved && <div className="stg-banner stg-banner--success">Preferences saved locally.</div>}
      <form className="stg-form" onSubmit={onSave}>
        <section className="stg-section">
          <h4 className="stg-section-title"><FiSliders size={15} /> Interface</h4>
          <div className="stg-form-row">
            <div className="stg-field">
              <label className="stg-label">Sidebar Default State</label>
              <select className="stg-input" name="sidebarDefault" value={prefs.sidebarDefault} onChange={onChange}>
                <option value="expanded">Expanded</option>
                <option value="collapsed">Collapsed</option>
              </select>
            </div>
            <div className="stg-field">
              <label className="stg-label">Table Density</label>
              <select className="stg-input" name="density" value={prefs.density} onChange={onChange}>
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
              </select>
            </div>
          </div>
          <div className="stg-form-row">
            <div className="stg-field">
              <label className="stg-label">Accent Color</label>
              <div className="stg-color-row">
                {['#6366f1','#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`stg-color-swatch${prefs.primaryColor === c ? ' stg-color-swatch--active' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setPrefs((p) => ({ ...p, primaryColor: c })); setSaved(false); }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="stg-section">
          <h4 className="stg-section-title">Regional</h4>
          <div className="stg-form-row">
            <div className="stg-field">
              <label className="stg-label">Timezone</label>
              <select className="stg-input" name="timezone" value={prefs.timezone} onChange={onChange}>
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="stg-field">
              <label className="stg-label">Date Format</label>
              <select className="stg-input" name="dateFormat" value={prefs.dateFormat} onChange={onChange}>
                {DATE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          </div>
        </section>

        <div className="form-actions">
          <button className="primary-btn" type="submit">Save Preferences</button>
        </div>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 5 — Danger Zone
══════════════════════════════════════════════════════════ */
function DangerTab() {
  const { logout } = useAuth();
  const [confirmPw, setConfirmPw] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDeactivate = async (e) => {
    e.preventDefault();
    if (!confirmPw) { setError('Enter your password to confirm.'); return; }
    setDeactivating(true); setError('');
    try {
      await deactivateAccount({ password: confirmPw });
      logout();
    } catch (e) {
      setError(e?.response?.data?.message || 'Deactivation failed.');
      setDeactivating(false);
    }
  };

  return (
    <div className="stg-tab-body">
      <section className="stg-section stg-section--danger">
        <h4 className="stg-section-title stg-section-title--danger"><FiAlertTriangle size={15} /> Sign Out</h4>
        <p className="stg-muted">Sign out of the current session on this browser.</p>
        <div className="form-actions" style={{ marginTop: 12 }}>
          <button className="danger-btn" onClick={logout}>Sign Out</button>
        </div>
      </section>

      <section className="stg-section stg-section--danger">
        <h4 className="stg-section-title stg-section-title--danger"><FiAlertTriangle size={15} /> Deactivate Account</h4>
        <p className="stg-muted">
          Permanently deactivates your account. This action cannot be undone and will revoke all your
          active sessions and access immediately.
        </p>
        {!showConfirm ? (
          <div className="form-actions" style={{ marginTop: 12 }}>
            <button className="danger-btn" onClick={() => setShowConfirm(true)}>Deactivate My Account</button>
          </div>
        ) : (
          <form className="stg-form" onSubmit={handleDeactivate} style={{ marginTop: 16 }}>
            <div className="stg-field">
              <label className="stg-label">Enter your password to confirm</label>
              <input
                className="stg-input"
                type="password"
                value={confirmPw}
                onChange={(e) => { setConfirmPw(e.target.value); setError(''); }}
                placeholder="Your current password"
                autoComplete="current-password"
              />
            </div>
            {error && <span className="stg-field-error">{error}</span>}
            <div className="form-actions">
              <button type="button" className="ghost-btn" onClick={() => { setShowConfirm(false); setConfirmPw(''); setError(''); }}>
                Cancel
              </button>
              <button className="danger-btn" type="submit" disabled={deactivating}>
                {deactivating ? 'Deactivating…' : 'Confirm Deactivation'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
const TABS = [
  { id: 'profile',       label: 'Profile',       icon: FiUser,         component: ProfileTab },
  { id: 'security',      label: 'Security',      icon: FiLock,         component: SecurityTab },
  { id: 'notifications', label: 'Notifications', icon: FiBell,         component: NotificationsTab },
  { id: 'preferences',   label: 'Preferences',   icon: FiSliders,      component: PreferencesTab },
  { id: 'danger',        label: 'Danger Zone',   icon: FiAlertTriangle,component: DangerTab },
];

function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const ActiveComponent = TABS.find((t) => t.id === activeTab)?.component || ProfileTab;

  return (
    <div className="stg-page">
      <div className="page-title-row">
        <h2>Settings</h2>
      </div>
      <div className="stg-shell">
        <nav className="stg-sidenav">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                className={`stg-nav-item${activeTab === tab.id ? ' stg-nav-item--active' : ''}${tab.id === 'danger' ? ' stg-nav-item--danger' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
        <div className="stg-content">
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
